import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  getGates,
  getUnits,
  inviteGuard,
  invitePropertyManager,
  inviteResident,
  inviteSupervisor,
} from '@/services/api.client';

export type InviteRole = 'resident' | 'guard' | 'security_supervisor' | 'property_manager';

const LABEL_FOR_ROLE: Record<InviteRole, string> = {
  resident: 'Invite Resident',
  guard: 'Invite Guard',
  security_supervisor: 'Invite Supervisor',
  property_manager: 'Invite Property Manager',
};

// Single schema with all optional fields — narrowed per-role in mutationFn.
const schema = z.object({
  fullName: z.string().min(2, 'Full name must be at least 2 characters'),
  email: z.string().email('Enter a valid email address'),
  phone: z.string().min(9, 'Enter a valid phone number'),
  unitId: z.string().optional(),
  gateId: z.string().optional(),
});
type FormValues = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  role: InviteRole;
}

export function InviteUserDialog({ open, onOpenChange, propertyId, role }: Props) {
  const qc = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const unitsQuery = useQuery({
    queryKey: ['units', propertyId],
    queryFn: () => getUnits(propertyId, { limit: 200 }),
    enabled: open && role === 'resident',
  });

  const gatesQuery = useQuery({
    queryKey: ['gates', propertyId],
    queryFn: () => getGates(propertyId),
    enabled: open && role === 'guard',
  });

  const mutation = useMutation({
    mutationFn: (values: FormValues) => {
      if (role === 'resident') {
        if (!values.unitId) throw new Error('Unit is required');
        return inviteResident(propertyId, {
          fullName: values.fullName,
          email: values.email,
          phone: values.phone,
          unitId: values.unitId,
        });
      }
      if (role === 'guard') {
        if (!values.gateId) throw new Error('Gate is required');
        return inviteGuard(propertyId, {
          fullName: values.fullName,
          email: values.email,
          phone: values.phone,
          gateId: values.gateId,
        });
      }
      if (role === 'property_manager') {
        return invitePropertyManager(propertyId, {
          fullName: values.fullName,
          email: values.email,
          phone: values.phone,
        });
      }
      return inviteSupervisor(propertyId, {
        fullName: values.fullName,
        email: values.email,
        phone: values.phone,
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['pm', 'users', propertyId] });
      toast.success(`Invitation sent — activation email queued`);
      onOpenChange(false);
      reset();
    },
    onError: (err) => {
      const status = (err as { status?: number }).status;
      if (status === 409) toast.error('A user with this email already exists');
      else if (status === 422) toast.error('Validation failed — check required fields');
      else toast.error('Failed to send invitation');
    },
  });

  const handleOpenChange = (next: boolean) => {
    onOpenChange(next);
    if (!next) reset();
  };

  const unitId = watch('unitId');
  const gateId = watch('gateId');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{LABEL_FOR_ROLE[role]}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            void handleSubmit((v) => mutation.mutate(v))(e);
          }}
        >
          <FieldGroup className="py-2">
            <Field>
              <FieldLabel htmlFor="fullName">Full Name</FieldLabel>
              <Input id="fullName" placeholder="Jane Kamau" {...register('fullName')} />
              <FieldError errors={[errors.fullName]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="email">Email</FieldLabel>
              <Input
                id="email"
                type="email"
                placeholder="jane@example.com"
                {...register('email')}
              />
              <FieldError errors={[errors.email]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="phone">Phone</FieldLabel>
              <Input id="phone" type="tel" placeholder="+254 700 000 000" {...register('phone')} />
              <FieldError errors={[errors.phone]} />
            </Field>

            {role === 'resident' && (
              <Field>
                <FieldLabel htmlFor="unitId">Unit</FieldLabel>
                {unitsQuery.isLoading ? (
                  <Skeleton className="h-9 w-full" />
                ) : unitsQuery.isError ? (
                  <p className="text-sm text-destructive">
                    Failed to load units. Please close and try again.
                  </p>
                ) : !unitsQuery.data?.data.length ? (
                  <div className="rounded-md border border-[var(--bg-surface-raised)] bg-[var(--bg-surface-raised)]/40 px-3 py-3 text-center">
                    <p className="text-sm text-muted-foreground">No units configured yet.</p>
                    <p className="text-xs text-muted-foreground/70 mt-1">
                      Add units in <span className="font-medium">Property Setup</span> before
                      inviting residents.
                    </p>
                  </div>
                ) : (
                  // Native select keeps userEvent.selectOptions test path
                  // consistent with existing org admin / enterprise dialogs.
                  <select
                    id="unitId"
                    aria-label="Unit"
                    value={unitId ?? ''}
                    onChange={(e) => setValue('unitId', e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select a unit…</option>
                    {unitsQuery.data?.data.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.unitNumber}
                      </option>
                    ))}
                  </select>
                )}
                {!unitId && errors.unitId === undefined && unitsQuery.data?.data.length ? (
                  <p className="text-xs text-[var(--text-tertiary)]">
                    Select a unit for this resident.
                  </p>
                ) : null}
              </Field>
            )}

            {role === 'guard' && (
              <Field>
                <FieldLabel htmlFor="gateId">Assigned Gate</FieldLabel>
                {gatesQuery.isLoading ? (
                  <Skeleton className="h-9 w-full" />
                ) : (
                  <select
                    id="gateId"
                    aria-label="Assigned Gate"
                    value={gateId ?? ''}
                    onChange={(e) => setValue('gateId', e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  >
                    <option value="">Select a gate…</option>
                    {gatesQuery.data?.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
          </FieldGroup>
          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                mutation.isPending ||
                (role === 'resident' && !unitId) ||
                (role === 'guard' && !gateId)
              }
            >
              {mutation.isPending ? 'Sending…' : 'Send Invitation'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
